import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Logo } from '@/components/ui/Logo';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/components/ui/use-toast';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { SupportTicketConsole } from '@/components/SupportTicketConsole';
import { MessageSquare } from 'lucide-react';

export function Footer() {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [showSupportModal, setShowSupportModal] = useState(false);

  const handleSupportClick = () => {
    if (!user) {
      toast({
        title: 'Authentication Required',
        description: 'Please log in to submit a support ticket.',
      });
      navigate('/login?redirect=/profile%23support-tickets');
      return;
    }
    setShowSupportModal(true);
  };

  return (
    <footer className="border-t bg-gray-50 mt-auto">
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand & Social Profiles */}
          <div className="space-y-4">
            <Logo iconClassName="h-6 w-6" textClassName="text-lg font-bold font-heading" />
            <p className="text-sm text-muted-foreground">
              Create professional invoices in minutes. Free and easy to use.
            </p>
            {/* Social Media Profiles */}
            <div className="pt-2">
              <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">Connect With Us</div>
              <div className="flex items-center gap-3">
                <a
                  href="https://www.facebook.com/QuickFreeInvoiceTool"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Follow QuickFreeInvoice on Facebook"
                  className="w-8 h-8 rounded-full bg-slate-200/70 hover:bg-blue-700 hover:text-white text-slate-700 flex items-center justify-center transition-all duration-200 shadow-sm"
                >
                  <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                    <path d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12c0 4.84 3.44 8.87 8 9.8V15H7.5v-3H10V9.5C10 7.01 11.49 5.6 13.73 5.6c1.07 0 2.19.19 2.19.19v2.41h-1.24c-1.23 0-1.61.77-1.61 1.56V12h2.72l-.43 3H13v6.8c4.56-.93 8-4.96 8-9.8z" />
                  </svg>
                </a>
              </div>
            </div>
          </div>

          {/* Product */}
          <div>
            <div className="font-semibold mb-3 text-slate-900">Product</div>
            <ul className="space-y-2">
              <li>
                <Link
                  to="/dashboard"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Dashboard
                </Link>
              </li>
              <li>
                <Link
                  to="/pricing"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Pricing
                </Link>
              </li>
              <li>
                <Link
                  to="/subscription"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Subscription
                </Link>
              </li>
              <li>
                <Link
                  to="/blog"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Invoicing Blog
                </Link>
              </li>
              <li>
                <Link
                  to="/templates"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Invoice Templates
                </Link>
              </li>
              <li>
                <Link
                  to="/guide"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Invoice Guide
                </Link>
              </li>
              <li>
                <Link
                  to="/help"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Help Center
                </Link>
              </li>
              <li>
                <Link
                  to="/faq"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  FAQ
                </Link>
              </li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <div className="font-semibold mb-3 text-slate-900">Company</div>
            <ul className="space-y-2">
              <li>
                <Link
                  to="/about"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  About Us
                </Link>
              </li>
              <li>
                <Link
                  to="/privacy"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link
                  to="/security"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Security
                </Link>
              </li>
              <li>
                <Link
                  to="/terms"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Terms & Conditions
                </Link>
              </li>
              <li>
                <Link
                  to="/refund"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Refund Policy
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact & Share */}
          <div>
            <div className="font-semibold mb-3 text-slate-900">Contact & Support</div>
            <ul className="space-y-2 mb-4">
              <li>
                <a
                  href="mailto:support@quickfreeinvoicetool.com"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  support@quickfreeinvoicetool.com
                </a>
              </li>
              <li>
                <button
                  onClick={handleSupportClick}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors text-left"
                >
                  Support Ticket Console
                </button>
              </li>
            </ul>

            {/* Social Share Buttons */}
            <div className="pt-2 border-t border-slate-200">
              <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">Share QuickFreeInvoice</div>
              <div className="flex items-center gap-2 flex-wrap">
                <a
                  href="https://www.facebook.com/sharer/sharer.php?u=https%3A%2F%2Fquickfreeinvoicetool.com%2F"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Share on Facebook"
                  className="px-2.5 py-1 text-xs font-medium bg-slate-200/80 hover:bg-blue-700 hover:text-white rounded-md transition-colors flex items-center gap-1.5 text-slate-700"
                >
                  <svg className="w-3 h-3 fill-current" viewBox="0 0 24 24">
                    <path d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12c0 4.84 3.44 8.87 8 9.8V15H7.5v-3H10V9.5C10 7.01 11.49 5.6 13.73 5.6c1.07 0 2.19.19 2.19.19v2.41h-1.24c-1.23 0-1.61.77-1.61 1.56V12h2.72l-.43 3H13v6.8c4.56-.93 8-4.96 8-9.8z" />
                  </svg>
                  Facebook
                </a>
                <a
                  href="https://twitter.com/intent/tweet?text=Create%20free%20PDF%20%26%20Word%20invoices%20in%2060s%20with%20QuickFreeInvoice!&url=https%3A%2F%2Fquickfreeinvoicetool.com%2F"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Share on Twitter / X"
                  className="px-2.5 py-1 text-xs font-medium bg-slate-200/80 hover:bg-indigo-600 hover:text-white rounded-md transition-colors flex items-center gap-1.5 text-slate-700"
                >
                  <svg className="w-3 h-3 fill-current" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                  Tweet
                </a>
                <a
                  href="https://www.linkedin.com/sharing/share-offsite/?url=https%3A%2F%2Fquickfreeinvoicetool.com%2F"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Share on LinkedIn"
                  className="px-2.5 py-1 text-xs font-medium bg-slate-200/80 hover:bg-blue-600 hover:text-white rounded-md transition-colors flex items-center gap-1.5 text-slate-700"
                >
                  <svg className="w-3 h-3 fill-current" viewBox="0 0 24 24">
                    <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.28 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.75M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z" />
                  </svg>
                  Share
                </a>
                <a
                  href="https://api.whatsapp.com/send?text=Create%20free%20PDF%20%26%20Word%20invoices%20in%2060s%20with%20QuickFreeInvoice!%20https%3A%2F%2Fquickfreeinvoicetool.com%2F"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Share on WhatsApp"
                  className="px-2.5 py-1 text-xs font-medium bg-slate-200/80 hover:bg-emerald-600 hover:text-white rounded-md transition-colors flex items-center gap-1.5 text-slate-700"
                >
                  WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t mt-8 pt-8 text-center text-sm text-muted-foreground">
          <p>© {new Date().getFullYear()} QuickFreeInvoice. All rights reserved.</p>
        </div>
      </div>

      {user && (
        <Dialog open={showSupportModal} onOpenChange={setShowSupportModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-primary" />
                Customer Support Desk
              </DialogTitle>
              <DialogDescription>
                Describe your issue or talk to our team. You can upload a screenshot to help us debug.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <SupportTicketConsole compact />
            </div>
          </DialogContent>
        </Dialog>
      )}
    </footer>
  );
}
